import type { FC } from 'react';
import { SEO_ORIGIN } from '@/base/constants';
import { useSettings } from '@/settings/api';
import { DEFAULT_SETTINGS } from '@/settings/config';

const BRAND = 'IQX';

// Stale prerendered <head> tags are stripped once, the first time PageMeta
// renders on the client — see the cleanup in the component body.
let prerenderedTagsCleared = false;

// Open Graph locale codes (language_TERRITORY) per supported language. Typed
// against SUPPORTED_LANGS so adding a language forces an entry here.
const OG_LOCALES = {
  en: 'en_US',
  ar: 'ar_IQ',
  ckb: 'ckb_IQ',
};

export const PageMeta: FC<{
  title?: string;
  description?: string;
  canonical?: string;
  /**
   * Language-agnostic path (e.g. `/price/btc`, `''` for the landing root). When
   * set, PageMeta emits a self-referencing canonical for the active language
   * plus the reciprocal hreflang set. Only public, lang-prefixed pages should
   * pass this — app/admin pages serve every language at the same URL.
   */
  localizedPath?: string;
  /**
   * Absolute or root-relative image URLs to emit as `<link rel="preload"
   * as="image">`. Pass build-imported asset URLs (hashed, cache-busted) so the
   * preload and the actual `<img src>` always resolve to the same file.
   */
  preloadImages?: string[];
}> = ({ title, description, canonical, localizedPath, preloadImages }) => {
  const [{ language }] = useSettings();

  const hasLocalized = typeof localizedPath === 'string';
  const localizedUrl = (lang: string) =>
    `${SEO_ORIGIN}/${lang}${localizedPath}`;
  const resolvedCanonical = hasLocalized ? localizedUrl(language) : canonical;

  // The public workspace is prerendered then mounted with createRoot (not
  // hydrateRoot, which was disabled to avoid hydration mismatches). createRoot
  // won't adopt the tags PageMeta baked into the prerendered <head>, so its
  // fresh render would leave a duplicate of every tag below. Strip the
  // prerendered copies once, before React commits this first render; React then
  // owns and recreates them. Non-prerendered loads have none, so it's a no-op.
  if (!prerenderedTagsCleared) {
    prerenderedTagsCleared = true;
    for (const el of document.head.querySelectorAll(
      'title, meta[name="description"], meta[property^="og:"], link[rel="canonical"], link[rel="alternate"][hreflang], link[rel="preload"][as="image"]',
    )) {
      el.remove();
    }
  }

  return (
    <>
      {preloadImages?.map((href) => (
        <link key={href} rel="preload" as="image" href={href} />
      ))}
      <title>{[title || '', BRAND].filter((x) => !!x).join(' | ')}</title>
      {description && <meta name="description" content={description} />}
      {resolvedCanonical && <link rel="canonical" href={resolvedCanonical} />}
      {resolvedCanonical && (
        <meta property="og:url" content={resolvedCanonical} />
      )}
      <meta property="og:locale" content={OG_LOCALES[language]} />
      {hasLocalized && (
        <>
          {Object.keys(OG_LOCALES).map((lang) => (
            <link
              key={lang}
              rel="alternate"
              hrefLang={lang}
              href={localizedUrl(lang)}
            />
          ))}
          <link
            rel="alternate"
            hrefLang="x-default"
            href={localizedUrl(DEFAULT_SETTINGS.language)}
          />
          {Object.keys(OG_LOCALES)
            .filter((lang) => lang !== language)
            .map((lang) => (
              <meta
                key={lang}
                property="og:locale:alternate"
                content={OG_LOCALES[lang as keyof typeof OG_LOCALES]}
              />
            ))}
        </>
      )}
    </>
  );
};
